import { Spinner } from "@components/common/icon";
import { toast, updateMyUserInfo } from "@utils/app";
import { refreshTheme } from "@utils/browser";
import { isBrowser } from "@utils/browser";
import { getQueryParams } from "@utils/helpers";
import { getBackendHostExternal } from "@utils/env";
import { IRoutePropsWithFetch } from "@utils/routes";
import { RouteData } from "@utils/types";
import { Component } from "inferno";
import { RouteComponentProps } from "inferno-router";
import { I18NextService, UserService } from "../../../services";
import { HttpService } from "../../../services/HttpService";
import { UnreadCounterService } from "../../../services/UnreadCounterService";
import { LoginResponse } from "lemmy-js-client";

interface SsoEnterProps {
  ticket?: string;
  redirect_after?: string;
}

export function getSsoEnterQueryParams(source?: string): SsoEnterProps {
  return getQueryParams<SsoEnterProps>(
    {
      ticket: (ticket?: string) => ticket,
      redirect_after: (redirect_after?: string) => redirect_after,
    },
    source,
  );
}

type SsoEnterRouteProps = RouteComponentProps<Record<string, never>> &
  SsoEnterProps;

export type SsoEnterConfig = IRoutePropsWithFetch<
  RouteData,
  Record<string, never>,
  SsoEnterProps
>;

/** One in-flight redemption per ticket; all route mounts share the same result. */
const ticketLoginCache = new Map<string, Promise<LoginResponse | null>>();
const failureToastShownForTicket = new Set<string>();

function resolveSsoProps(props: SsoEnterRouteProps): SsoEnterProps {
  const fromProps = {
    ticket: props.ticket,
    redirect_after: props.redirect_after,
  };
  if (fromProps.ticket || !isBrowser()) {
    return fromProps;
  }
  return {
    ...fromProps,
    ...getSsoEnterQueryParams(window.location.search),
  };
}

function loginWithAimooTicket(ticket: string): Promise<LoginResponse | null> {
  const cached = ticketLoginCache.get(ticket);
  if (cached) {
    return cached;
  }

  const promise = (async (): Promise<LoginResponse | null> => {
    try {
      const res = await fetch(
        `${getBackendHostExternal()}/aimoo/v1/users/sso/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ticket, stay_logged_in: true }),
        },
      );
      if (!res.ok) {
        return null;
      }
      const loginRes = (await res.json()) as LoginResponse;
      return loginRes.jwt ? loginRes : null;
    } catch {
      return null;
    }
  })();

  ticketLoginCache.set(ticket, promise);
  return promise;
}

export class SsoEnter extends Component<SsoEnterRouteProps, object> {
  private unmounted = false;
  private started = false;

  componentDidMount() {
    void this.startSso();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  async startSso() {
    if (this.started) {
      return;
    }
    this.started = true;

    const { ticket, redirect_after } = resolveSsoProps(this.props);
    if (!ticket) {
      this.failSso();
      return;
    }

    const loginRes = await loginWithAimooTicket(ticket);
    if (!loginRes?.jwt) {
      if (!UserService.Instance.auth()) {
        this.failSso(ticket);
      }
      return;
    }

    if (this.unmounted) {
      return;
    }

    UserService.Instance.login({ res: loginRes, showToast: false });

    try {
      const [site, myUser] = await Promise.all([
        HttpService.client.getSite(),
        HttpService.client.getMyUser(),
      ]);
      if (site.state === "success" && myUser.state === "success") {
        updateMyUserInfo(myUser.data);
        refreshTheme();
      }
    } catch {
      // JWT is already set; profile refresh failure should not block SSO.
    }

    if (this.unmounted) {
      return;
    }

    this.props.history.replace(redirect_after ?? "/");
    await UnreadCounterService.Instance.updateUnreadCounts();
  }

  failSso(ticket?: string) {
    if (this.unmounted || UserService.Instance.auth()) {
      return;
    }
    if (ticket && failureToastShownForTicket.has(ticket)) {
      return;
    }
    if (ticket) {
      failureToastShownForTicket.add(ticket);
    }
    toast(I18NextService.i18n.t("oauth_authorization_invalid"), "danger");
    this.props.history.replace("/login");
  }

  render() {
    return (
      <div className="container-lg">
        <Spinner />
      </div>
    );
  }
}
